import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { of, Subscription, throwError, timer, Subject } from 'rxjs';
import { catchError, filter, finalize, switchMap, retryWhen, mergeMap, takeWhile, tap } from 'rxjs/operators';
import * as Bowser from 'bowser';

import { environment } from '@env/environment';
import { untilDestroyed, readFileAsBinaryString } from '@app/core';
import { ActionEnum, CloudSignaturesEnum, Document, SecurityVerificationEnum, SignatureFormat, SignatureTypesEnum, SlimDocument, SlimOrganization, User, Signer, GraphQLResponse } from '@app/models';
import {
  AppService,
  CertificateService,
  DateService,
  DocumentService,
  ErrorHandlerService,
  NotyService,
  OrganizationService,
  PadesService,
  PadesSignatureData,
  UserService,
  WhitelabelService,
  EnterpriseService
} from '@app/services';
import { SetMissingUserInfoModalService } from '@app/shared';

export type ModalResult = PadesSignatureData;
export interface ModalPublicProperties {
  action: ActionEnum;
  document: SlimDocument | Document;
  signer?: Signer;
  padesSignatureId: string;
  hasFailedBiometry?: boolean;
  isSignerQualified?: boolean;
  showA3Option?: boolean;
}

enum ModalType {
  preSign,
  qualifiedOptions,
  a1,
  safeId,
  a3,
  vidaas,
  birdid,
  dscloud
}
enum A1FormStep {
  upload,
  password
}
enum SafeIdFormStep {
  cpfCnpj,
  waitingAuthorization,
  password
}
enum DefaultCloudFormStep {
  cpfCnpj,
  waitingAuthorization
}

@Component({
  selector: 'app-pre-sign-modal',
  templateUrl: './pre-sign-modal.component.html',
  styleUrls: ['./pre-sign-modal.component.scss'],
  standalone: false
})
export class PreSignModalComponent implements ModalPublicProperties, AfterViewInit, OnDestroy {
  @Input() action: ActionEnum;
  @Input() padesSignatureId: string;
  @Input() document: SlimDocument | Document;
  @Input() signer?: Signer;
  @Input() hasFailedBiometry: boolean;
  @Input() isSignerQualified: boolean;
  @Input() showA3Option = true;
  preSignForm!: UntypedFormGroup;
  a1Form!: UntypedFormGroup;
  a3Form!: UntypedFormGroup;
  safeIdForm!: UntypedFormGroup;
  defaultCloudForm!: UntypedFormGroup;
  modalType: ModalType;
  a1FormStep = A1FormStep.upload;
  safeIdFormStep = SafeIdFormStep.cpfCnpj;
  defaultCloudFormStep = DefaultCloudFormStep.cpfCnpj;
  currentUser: User;
  organizations: SlimOrganization[] = [];
  format: SignatureFormat;
  certInfo: { serial?: string; cn?: string; o?: string; type?: CloudSignaturesEnum };
  isLoading = false;
  canSelectPkcs11Library = false;
  signatureNotReady = false;
  currentSessionId: string | null = null;
  isA3DesktopActionDisabled = false;
  readonly isElectron = environment.electron;
  readonly ModalType = ModalType;
  readonly A1FormStep = A1FormStep;
  readonly SafeIdFormStep = SafeIdFormStep;
  readonly DefaultCloudFormStep = DefaultCloudFormStep;
  readonly ActionEnum = ActionEnum;
  readonly SignatureFormat = SignatureFormat;
  readonly cloudSignatureInfo: { [key: string]: { modalType: ModalType; label: string } } = {
    [CloudSignaturesEnum.Safeid]: {
      modalType: ModalType.safeId,
      label: 'SafeID'
    },
    [CloudSignaturesEnum.Vidaas]: {
      modalType: ModalType.vidaas,
      label: 'VIDaaS'
    },
    [CloudSignaturesEnum.Birdid]: {
      modalType: ModalType.birdid,
      label: 'BirdID'
    },
    [CloudSignaturesEnum.Dscloud]: {
      modalType: ModalType.dscloud,
      label: 'DSCloud'
    }
  };
  readonly cloudSignatureLabels: { [key: number]: string };
  private a3DesktopActionCooldownSubscription?: Subscription;
  private cancelPolling$ = new Subject<void>();
  @ViewChild('uploadWrapper', { static: false }) private uploadWrapper: ElementRef<HTMLElement>;
  @ViewChild('a1PasswordInput', { static: false }) private a1PasswordInput: ElementRef<HTMLInputElement>;
  @ViewChild('a3PasswordInput', { static: false }) private a3PasswordInput: ElementRef<HTMLInputElement>;
  @ViewChild('safeidPasswordInput', { static: false }) private safeidPasswordInput: ElementRef<HTMLInputElement>;
  @ViewChild('safeidCpfCnpjInput', { static: false }) private safeidCpfCnpjInput: ElementRef<HTMLInputElement>;
  @ViewChild('defaultCloudCpfCnpjInput', { static: false }) private defaultCloudCpfCnpjInput: ElementRef<HTMLInputElement>;

  constructor(
    public modal: NgbActiveModal,
    public documentService: DocumentService,
    public dateService: DateService,
    public appService: AppService,
    public enterpriseService: EnterpriseService,
    public whitelabelService: WhitelabelService,
    private certificateService: CertificateService,
    private formBuilder: UntypedFormBuilder,
    private notyService: NotyService,
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    private padesService: PadesService,
    private organizationService: OrganizationService,
    private userService: UserService,
    private setMissingUserInfoModalService: SetMissingUserInfoModalService
  ) {
    this.preSignForm = this.formBuilder.group({ organization: null });
    this.a1Form = this.formBuilder.group({ file: null, password: '' });
    this.a3Form = this.formBuilder.group({ password: '' });
    this.safeIdForm = this.formBuilder.group({ cpfCnpj: '', password: '' });
    this.defaultCloudForm = this.formBuilder.group({ cpfCnpj: '', otp: '' });
    this.cloudSignatureLabels = Object.keys(this.cloudSignatureInfo).reduce((acc, key) => {
      const { modalType, label } = this.cloudSignatureInfo[key];
      return { ...acc, [modalType]: label };
    }, {});
  }

  ngAfterViewInit() {
    const isQualifiedSignature = this.document?.qualified || this.isSignerQualified;
    if (!this.document || !isQualifiedSignature) {
      setTimeout(() => (this.modalType = ModalType.preSign));
    } else {
      setTimeout(() => (this.isLoading = true));
    }
    this.uploadWrapper.nativeElement.addEventListener('dragover', event => event.preventDefault(), false);
    this.uploadWrapper.nativeElement.addEventListener(
      'drop',
      event => {
        event.stopPropagation();
        event.preventDefault();
        const files: FileList = event.dataTransfer.files;

        if (files && files[0] && files[0].type.match('application/x-pkcs')) {
          this.a1Form.get('file').setValue(files[0]);
          this.padesNextStep();
        } else {
          this.notyService.error(this.translateService.instant('notyService.invalidCertificateFile'));
        }
      },
      false
    );

    this.organizationService.getOrganizations().subscribe({
      next: organizations => (this.organizations = organizations)
    });

    this.userService.getCurrentUser().subscribe({
      next: user => {
        this.currentUser = user;
        this.preSignForm.get('organization').setValue(this.currentUser.organization && this.currentUser.organization.id);

        this.format = this.currentUser.settings.format as SignatureFormat;

        if (this.document && this.document.configs && this.document.configs.signature_appearance) {
          this.format = this.documentService.signatureAppearanceToFormats[this.document.configs.signature_appearance];
        }

        if (this?.document && (this?.document.qualified || this?.isSignerQualified)) {
          const certData = this.padesService.getStoredA1CertificateData(this.currentUser);
          if (certData && certData.file && certData.password) {
            setTimeout(() => (this.isLoading = false));
            this.modalType = ModalType.preSign;
            this.a1Form.setValue({ file: certData.file, password: certData.password });
            const certificate = this.padesService.parseA1Certificate(certData.file, certData.password);
            this.certInfo = certificate ? { cn: this.certificateService.getSubjectField(certificate, 'CN'), o: this.certificateService.getSubjectField(certificate, 'O') } : null;
          } else {
            this.padesService
              .checkCloudCert({ fetchPolicy: 'cache-first' })
              .pipe(catchError(() => of(null)))
              .subscribe({
                next: data => {
                  setTimeout(() => (this.isLoading = false));
                  if (data?.serial_number) {
                    this.certInfo = { serial: data.serial_number, type: data.type };
                    this.modalType = ModalType.preSign;
                  } else {
                    this.modalType = ModalType.qualifiedOptions;
                  }
                }
              });
          }
        }
      }
    });
  }

  ngOnDestroy() {
    this.a3DesktopActionCooldownSubscription?.unsubscribe();
    this.cancelPolling$.next();
    this.cancelPolling$.complete();
  }

  goBack() {
    this.signatureNotReady = false;
    this.cancelPolling$.next();
    this.modalType = ModalType.qualifiedOptions;
  }

  selectA3Desktop() {
    this.modalType = ModalType.a3;
    this.padesService.generateSignatureSession(this.padesSignatureId).subscribe({
      next: (sessionId: string) => {
        this.currentSessionId = sessionId;
      },
      error: error => this.errorHandlerService.handle(error)
    });
  }

  get currentActionTranslationKey(): string {
    return `signature.signAction.${(this.action || ActionEnum.Sign).toLowerCase()}`;
  }

  get currentActionDocumentTranslationKey(): string {
    return `signature.signActionDocument.${(this.action || ActionEnum.Sign).toLowerCase()}`;
  }

  get canEditSignerInfo(): boolean {
    const blockedTypes = [
      SecurityVerificationEnum.Manual,
      SecurityVerificationEnum.Upload,
      SecurityVerificationEnum.Live,
      SecurityVerificationEnum.PfFacial,
      SecurityVerificationEnum.PfFacialMatch,
      SecurityVerificationEnum.BiometricAndTextExtraction,
      SecurityVerificationEnum.LivenessAndTextExtraction
    ];
    return !(this.signer?.verifications || []).some(v => blockedTypes.includes(v?.type as SecurityVerificationEnum));
  }

  padesNextStep() {
    if (this.modalType === ModalType.preSign) {
      this.chooseOrganization();
    } else if (this.modalType === ModalType.a1) {
      if (this.a1FormStep === A1FormStep.password) {
        this.a1Form.markAllAsTouched();
        const file = this.a1Form.get('file').value;
        (typeof file === 'string' ? of(file) : readFileAsBinaryString(file)).subscribe({
          next: fileData => {
            try {
              const certificate = this.padesService.parseA1Certificate(fileData, this.a1Form.get('password').value);
              this.certInfo = certificate ? { cn: this.certificateService.getSubjectField(certificate, 'CN'), o: this.certificateService.getSubjectField(certificate, 'O') } : null;
              this.modalType = ModalType.preSign;
            } catch (err: any) {
              if (/password/i.test(err?.message)) {
                this.a1FormStep = A1FormStep.password;
                this.a1Form.get('password').setErrors({ server: this.translateService.instant('error_incorrect_password') });
                this.focusPasswordInput();
              } else {
                this.a1Form.get('password').setErrors({ server: err?.message || err });
                throw err;
              }
            }
          }
        });
      } else {
        this.a1FormStep = A1FormStep.password;
        this.focusPasswordInput();
      }
    } else if (this.modalType === ModalType.a3) {
      this.a3Form.markAllAsTouched();
      this.signPades();
    } else if (this.modalType === ModalType.safeId) {
      if (this.safeIdFormStep === SafeIdFormStep.cpfCnpj) {
        const waitingErrors = ['cloud_cert_not_found', 'cloud_cert_app_not_authorized', 'cloud_cert_app_expired', 'cloud_cert_sign_expired'];
        const successErrors = ['cloud_cert_sign_not_authorized', 'cloud_cert_sign_expired'];
        this.safeIdFormStep = SafeIdFormStep.waitingAuthorization;

        this.padesService
          .checkCloudCert({ fetchPolicy: 'network-only' })
          .pipe(
            switchMap(data => {
              // Se veio um cert, mas não é do tipo SafeID, trata como "ainda não autorizado"
              if (data?.serial_number && data?.type !== CloudSignaturesEnum.Safeid) {
                return throwError(() => ({ errors: [{ message: 'cloud_cert_not_found' }] }) as GraphQLResponse);
              }
              return of(data);
            }),
            retryWhen(
              mergeMap((error, i) => {
                const retryAttempt = i + 1;
                const handledError = this.errorHandlerService.handle(error, { ignoreNotificationOn: waitingErrors.concat(successErrors) });
                if (waitingErrors.includes(handledError.message) && retryAttempt === 1) {
                  this.padesService.authorizeCloudCertApp({ cpfCnpj: this.safeIdForm.get('cpfCnpj').value, type: CloudSignaturesEnum.Safeid }).subscribe({
                    error: currentError => this.errorHandlerService.handle(currentError)
                  });
                } else if (!waitingErrors.includes(handledError.message)) {
                  return throwError(() => handledError);
                } else if (retryAttempt > 50 || this.safeIdFormStep !== SafeIdFormStep.waitingAuthorization) {
                  return throwError(() => handledError);
                }
                return timer((10 + retryAttempt) * 1000);
              })
            ),
            catchError(handledError => (successErrors.includes(handledError && handledError.message) ? of(null) : throwError(() => handledError))),
            takeWhile(() => this.safeIdFormStep === SafeIdFormStep.waitingAuthorization),
            untilDestroyed(this)
          )
          .subscribe({
            next: () => {
              this.safeIdFormStep = SafeIdFormStep.password;
              this.focusPasswordInput();
            },
            error: () => {
              this.safeIdFormStep = SafeIdFormStep.cpfCnpj;
              this.focusCpfCnpjInput();
            }
          });
      } else if (this.safeIdFormStep === SafeIdFormStep.password) {
        this.safeIdForm.markAllAsTouched();
        this.signPades();
      } else {
        this.safeIdFormStep = SafeIdFormStep.password;
        this.focusPasswordInput();
      }
    } else if ([ModalType.vidaas, ModalType.dscloud].includes(this.modalType)) {
      if (this.defaultCloudFormStep === DefaultCloudFormStep.cpfCnpj) {
        const type = Object.keys(this.cloudSignatureInfo).find(key => this.cloudSignatureInfo[key].modalType === this.modalType) as CloudSignaturesEnum;
        const waitingErrors = ['cloud_cert_not_found', 'cloud_cert_app_not_authorized', 'cloud_cert_app_expired', 'cloud_cert_sign_expired'];
        const successErrors = ['cloud_cert_sign_not_authorized', 'cloud_cert_sign_expired'];
        this.defaultCloudFormStep = DefaultCloudFormStep.waitingAuthorization;
        this.padesService
          .checkAuthorization({ type })
          .pipe(
            retryWhen(
              mergeMap((error, i) => {
                const retryAttempt = i + 1;
                const handledError = this.errorHandlerService.handle(error, {
                  ignoreNotificationOn: waitingErrors.concat(successErrors)
                });
                if (waitingErrors.includes(handledError.message) && retryAttempt === 1) {
                  this.padesService
                    .authorizeCloudCertApp({
                      cpfCnpj: this.defaultCloudForm.get('cpfCnpj').value,
                      type
                    })
                    .subscribe({
                      next: data => {
                        if (this.modalType === ModalType.dscloud && data.redirect_url) {
                          window.open(data.redirect_url, '_blank');
                        }
                      },
                      error: currentError => this.errorHandlerService.handle(currentError)
                    });
                } else if (!waitingErrors.includes(handledError.message)) {
                  return throwError(() => handledError);
                } else if (retryAttempt > 50 || this.defaultCloudFormStep !== DefaultCloudFormStep.waitingAuthorization) {
                  return throwError(() => handledError);
                }
                return timer((10 + retryAttempt) * 1000);
              })
            )
          )
          .subscribe({
            next: data => {
              if (data) {
                this.safeIdForm.markAllAsTouched();
                this.signPades();
              }
            },
            error: () => {
              this.defaultCloudFormStep = DefaultCloudFormStep.cpfCnpj;
              this.focusCpfCnpjInput();
            }
          });
      }
    } else if ([ModalType.birdid].includes(this.modalType)) {
      if (this.defaultCloudFormStep === DefaultCloudFormStep.cpfCnpj) {
        this.isLoading = true;
        this.padesService
          .authorizeCloudCertApp({ cpfCnpj: this.defaultCloudForm.get('cpfCnpj').value, type: CloudSignaturesEnum.Birdid, otp: this.defaultCloudForm.get('otp').value })
          .pipe(finalize(() => (this.isLoading = false)))
          .subscribe({
            next: data => {
              if (data.status) {
                this.signPades();
              }
            },
            error: currentError => this.errorHandlerService.handle(currentError)
          });
      }
    }
  }

  chooseOrganization() {
    const id = this.preSignForm.get('organization').value;
    const isQualifiedSignature = this.document?.qualified || this.isSignerQualified;

    const done = () => {
      if (isQualifiedSignature) {
        if (this.certInfo?.type) {
          this.modalType = this.cloudSignatureInfo[this.certInfo.type].modalType;
          this.signPades();
        } else if (this.certInfo?.cn) {
          this.modalType = ModalType.a1;
          this.signPades();
        } else {
          // No certificate info available, show qualified options menu
          this.modalType = ModalType.qualifiedOptions;
        }
      } else {
        this.modal.close({} as PadesSignatureData);
      }
    };

    if (this.currentUser.organization && id !== this.currentUser.organization.id) {
      this.isLoading = true;
      this.preSignForm.markAllAsTouched();

      this.organizationService
        .selectDefault({ id })
        .pipe(
          switchMap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' })),
          finalize(() => (this.isLoading = false))
        )
        .subscribe({
          next: () => done(),
          error: error => this.errorHandlerService.handle(error)
        });
    } else {
      done();
    }
  }

  signPades(modalType?: ModalType) {
    modalType = modalType || this.modalType;
    const currentSigner =
      this.signer || ((this.document as Document)?.signatures || []).find(sig => sig?.user?.id === this.currentUser?.id && this.documentService.isSignerPending(sig, { ignoreEmailErrors: true }));
    const type = {
      [ModalType.a1]: SignatureTypesEnum.A1,
      [ModalType.a3]: SignatureTypesEnum.A3,
      [ModalType.safeId]: SignatureTypesEnum.Safeid,
      [ModalType.vidaas]: SignatureTypesEnum.Vidaas,
      [ModalType.birdid]: SignatureTypesEnum.Birdid,
      [ModalType.dscloud]: SignatureTypesEnum.Dscloud
    }[modalType as number];
    const formValue = {
      [ModalType.a1]: this.a1Form,
      [ModalType.a3]: this.a3Form,
      [ModalType.safeId]: this.safeIdForm,
      [ModalType.vidaas]: this.defaultCloudForm,
      [ModalType.birdid]: this.defaultCloudForm,
      [ModalType.dscloud]: this.defaultCloudForm
    }[modalType as number].value;

    if (type === SignatureTypesEnum.A1 && (!formValue.file || (typeof formValue.file !== 'string' && !formValue.file.type.match('application/x-pkcs')))) {
      this.a1FormStep = A1FormStep.upload;
      this.a1Form.get('file').setErrors({ server: this.translateService.instant('error_invalid_certificate_file') });
    } else {
      this.isLoading = true;
      this.padesService
        .prepareToSignPades({
          type,
          user: this.currentUser,
          signatureId: this.padesSignatureId,
          signer: currentSigner as Signer,
          document: this.document as Document,
          ...formValue
        })
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: data => this.modal.close(data as PadesSignatureData),
          error: error => {
            if (type === SignatureTypesEnum.Safeid) {
              if (error) {
                this.errorHandlerService.handleValidation(this.safeIdForm, error);
              }
              if (!this.safeIdForm.get('password').errors) {
                this.safeIdForm.get('password').setErrors({ server: this.translateService.instant('error_incorrect_password') });
              }
            } else if (type === SignatureTypesEnum.A3) {
              this.canSelectPkcs11Library = false;
              if (error && typeof error.message === 'string' && error.message.match(/error_pkcs11/i)) {
                if (error.message.match(/error_pkcs11_could_not_load_module/i)) {
                  this.canSelectPkcs11Library = true;
                  this.errorHandlerService.handle(error, { ignoreNotificationOn: ['error_pkcs11_could_not_load_module'] });
                } else {
                  this.focusPasswordInput();
                  this.a3Form.get('password').setErrors({ server: this.translateService.instant(error.message) });
                }
              } else {
                this.canSelectPkcs11Library = true;
                this.errorHandlerService.handle(error);
              }
            } else if ([SignatureTypesEnum.Vidaas, SignatureTypesEnum.Birdid].includes(type)) {
              if (error) {
                this.errorHandlerService.handleValidation(this.defaultCloudForm, error);
              }
            } else {
              console.log(error && error.message);
              console.log('File: ', formValue.file && formValue.file.name, formValue.file && formValue.file.type, formValue.file && formValue.file.size);
              if (error && typeof error.message === 'string' && (error.message.match(/wrong password/i) || error.message.match(/Invalid password/i))) {
                this.a1FormStep = A1FormStep.password;
                this.a1Form.get('password').setErrors({ server: this.translateService.instant('error_incorrect_password') });
                this.focusPasswordInput();
              } else if (error && typeof error.message === 'string' && (error.message.match(/failed to decrypt/i) || error.message.match(/too few bytes/i) || error.message.match(/bits supported/i))) {
                this.a1FormStep = A1FormStep.upload;
                this.a1Form.get('file').setErrors({ server: this.translateService.instant('error_corrupted_certificate', { description: error.message }) });
              } else if (error && typeof error.message === 'string' && error.message.match(/certificate expired/i)) {
                this.a1FormStep = A1FormStep.upload;
                this.a1Form.get('file').setErrors({ server: this.translateService.instant('error_expired_certificate') });
              } else {
                this.a1FormStep = A1FormStep.upload;
                this.errorHandlerService.handle(error);
              }
            }
          }
        });
    }
  }

  focusCpfCnpjInput() {
    if (this.modalType === ModalType.safeId) {
      setTimeout(() => this.safeidCpfCnpjInput && this.safeidCpfCnpjInput.nativeElement && this.safeidCpfCnpjInput.nativeElement.focus());
    }
    if ([ModalType.vidaas, ModalType.birdid, ModalType.dscloud].includes(this.modalType)) {
      setTimeout(() => this.defaultCloudCpfCnpjInput && this.defaultCloudCpfCnpjInput.nativeElement && this.defaultCloudCpfCnpjInput.nativeElement.focus());
    }
  }

  focusPasswordInput() {
    if (this.modalType === ModalType.a1) {
      setTimeout(() => this.a1PasswordInput && this.a1PasswordInput.nativeElement && this.a1PasswordInput.nativeElement.focus());
    } else if (this.modalType === ModalType.a3) {
      setTimeout(() => this.a3PasswordInput && this.a3PasswordInput.nativeElement && this.a3PasswordInput.nativeElement.focus());
    } else if (this.modalType === ModalType.safeId) {
      setTimeout(() => this.safeidPasswordInput && this.safeidPasswordInput.nativeElement && this.safeidPasswordInput.nativeElement.focus());
    }
  }

  downloadDesktopApp() {
    if (Bowser.getParser(window.navigator.userAgent).getOSName() === 'Windows') {
      window.open(environment.hsmBridgeDownloadUrl);
    } else {
      this.notyService.error(this.translateService.instant('notyService.desktopAppWindowsOnly'));
    }
  }

  selectPkcs11Lib() {
    this.padesService
      .selectPkcs11LibDialog()
      .pipe(filter(libPath => !!libPath))
      .subscribe({
        next: () => {
          this.canSelectPkcs11Library = false;
          this.padesNextStep();
        }
      });
  }

  // Force the input element to reload.
  dispatchInputEvent(input: HTMLInputElement) {
    input?.dispatchEvent(new Event('input'));
  }

  copyDesktopSessionId() {
    this.notyService.success(this.translateService.instant('notyService.copySuccess'));
  }

  isAutnq(user?: User) {
    user = user || this.currentUser;
    return !!(user && (this.currentUser.email || '').match(/@autentique.com.br$/));
  }

  isInternalA3User(user?: User) {
    user = user || this.currentUser;
    return !!user && /@(autentique\.com\.br|pipefy\.com)$/.test(user.email || '');
  }

  isWhitelabel() {
    return !this.appService.isAutentiqueDomain() && !!this.whitelabelService?.whitelabel?.domain;
  }

  openSetMissingUserInfoModal() {
    this.setMissingUserInfoModalService
      .openIfMissingUserInfo({
        ignoreEmailAndPassword: true,
        ignoreLocale: true,
        forceEdit: true,
        ignoreCpf: this.document.ignore_cpf,
        ignoreBirthdate: this.document.ignore_birthdate
      })
      .subscribe({
        next: data => {
          if (data) {
            this.currentUser = { ...this.currentUser, ...data };
          }
        }
      });
  }

  openDesktopSignatureApp() {
    if (this.isA3DesktopActionDisabled) {
      return;
    }

    if (!this.currentSessionId) {
      this.notyService.error(this.translateService.instant('notyService.desktopSignatureSessionNotReady'));
      return;
    }

    this.disableA3DesktopActionsTemporarily();

    this.padesService
      .startPollingDesktopSignatureStatus(this.padesSignatureId, this.cancelPolling$)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: status => {
          if (status.signed) {
            this.closeDesktopSignatureSuccess();
          } else if (status.timedOut) {
            this.signatureNotReady = true;
          }
        }
      });

    const deepLink = `hsmbridge://sign?sessionToken=${encodeURIComponent(this.currentSessionId)}`;
    window.location.href = deepLink;
  }

  // Performs a one-time signature status check
  checkDesktopSignatureStatusOnce() {
    if (this.isA3DesktopActionDisabled) {
      return;
    }

    this.disableA3DesktopActionsTemporarily();

    this.padesService
      .checkDesktopSignatureStatus(this.padesSignatureId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: signed => {
          if (signed) {
            this.closeDesktopSignatureSuccess();
          } else {
            this.signatureNotReady = true;
          }
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  private disableA3DesktopActionsTemporarily() {
    this.isA3DesktopActionDisabled = true;

    this.a3DesktopActionCooldownSubscription?.unsubscribe();
    this.a3DesktopActionCooldownSubscription = timer(3000).subscribe(() => {
      this.isA3DesktopActionDisabled = false;
      this.a3DesktopActionCooldownSubscription = undefined;
    });
  }

  private closeDesktopSignatureSuccess() {
    this.modal.close({
      type: SignatureTypesEnum.A3,
      signer: this.signer as Signer,
      document: this.document as Document,
      isA3DesktopSuccess: true
    } as ModalResult);
  }
}
